import { PostMessage } from "@bt-common/interfaces/post-message.interface";
import { EventEmitter } from "events";
import { ReplySafeEmitter } from "./reply-safe-emitter";
import { AUTH_BRIDGE_CMDS, AUTH_SENSITIVE_CMDS, isTrustedAuthOrigin } from "@bt-common/interfaces/auth-bridge-contracts.interface";
import { MessageCommand, MessageCommandArgs, MessageCommandResult } from "@bt-common/interfaces/message-command-contracts.interface";

export type SendResponseFunction = (messageResponse?: unknown) => void;
export interface MessageInterface {
  // Known commands (MessageCommandContractMap) get literal autocomplete; arbitrary
  // strings still allowed for legacy/forwarded cmds.
  cmd: MessageCommand | (string & {});
  [key: string]: unknown;
}

// Helper function to protect a tab from auto-discard with verification (DRY principle)
function protectTabFromAutoDiscard(sender: chrome.runtime.MessageSender, context: string) {
  if (sender.tab && sender.tab.url?.includes('web.whatsapp.com')) {
    try {
      const tabId = sender.tab.id!;
      const wasAutoDiscardable = sender.tab.autoDiscardable;
      const wasDiscarded = sender.tab.discarded;
      
      console.log(`[TabPrevent] ${context} tab ${tabId} state → discarded: ${wasDiscarded}, autoDiscardable: ${wasAutoDiscardable}`);
      
      if (sender.tab.autoDiscardable) {
        chrome.tabs.update(tabId, { autoDiscardable: false }, (updatedTab) => {
          if (chrome.runtime.lastError) {
            console.log(`[TabPrevent] Error setting autoDiscardable for ${context.toLowerCase()} tab ${tabId}: ${chrome.runtime.lastError.message}`);
          } else {
            // Verify the change by querying the tab again
            chrome.tabs.get(tabId, (verifiedTab) => {
              if (chrome.runtime.lastError) {
                console.log(`[TabPrevent] ${context} tab ${tabId} protected from auto-discard (verification failed: ${chrome.runtime.lastError.message})`);
              } else {
                console.log(`[TabPrevent] ${context} tab ${tabId} VERIFIED → discarded: ${verifiedTab.discarded}, autoDiscardable: ${verifiedTab.autoDiscardable} ✅`);
              }
            });
          }
        });
      } else {
        console.log(`[TabPrevent] ${context} tab ${tabId} already protected (autoDiscardable: false) ✅`);
      }
    } catch (error) {
      // Silently handle any permission errors
      console.debug(`[TabPrevent] Could not update ${context.toLowerCase()} tab autoDiscardable: ${error}`);
    }
  }
}
/**
 * Safely clones data for postMessage, handling circular references and special types.
 * Uses a WeakSet to track visited objects and prevent infinite recursion.
 * Also has a max depth limit as additional protection against deeply nested structures.
 */
function safeCloneForPostMessage(data: any, visited: WeakSet<object> = new WeakSet(), depth: number = 0): any {
  // Max depth protection to prevent extremely deep recursion
  const MAX_DEPTH = 50;
  if (depth > MAX_DEPTH) {
    console.warn('[safeCloneForPostMessage] Max depth exceeded, truncating');
    return '[MAX_DEPTH_EXCEEDED]';
  }

  // Handle null and undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitives (string, number, boolean, symbol, bigint)
  if (typeof data !== 'object' && typeof data !== 'function') {
    return data;
  }

  // Handle functions - cannot be cloned for postMessage
  if (typeof data === 'function') {
    return undefined;
  }

  // Handle special built-in types
  if (data instanceof URL) {
    return data.toString();
  }
  if (data instanceof Blob) {
    return data; // Blob is transferable
  }
  if (data instanceof Date) {
    return data.toISOString();
  }
  if (data instanceof RegExp) {
    return data.toString();
  }
  if (data instanceof Error) {
    return { name: data.name, message: data.message, stack: data.stack };
  }
  if (data instanceof Map) {
    const obj: Record<string, any> = {};
    data.forEach((value, key) => {
      obj[String(key)] = safeCloneForPostMessage(value, visited, depth + 1);
    });
    return obj;
  }
  if (data instanceof Set) {
    return Array.from(data).map(item => safeCloneForPostMessage(item, visited, depth + 1));
  }

  // Handle circular references - check if we've already visited this object
  if (visited.has(data)) {
    return '[CIRCULAR_REFERENCE]';
  }

  // Mark this object as visited
  visited.add(data);

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => safeCloneForPostMessage(item, visited, depth + 1));
  }

  // Handle plain objects
  const cloned: any = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      try {
        cloned[key] = safeCloneForPostMessage(data[key], visited, depth + 1);
      } catch (e) {
        // Skip properties that throw errors when accessed (getters that fail)
        console.debug(`[safeCloneForPostMessage] Skipping property ${key} due to error:`, e);
      }
    }
  }
  return cloned;
}
export class MessageService {
  protected static instance: MessageService;
  protected eventEmitter: EventEmitter
  protected source: string;

  // Make constructor protected to allow inheritance
  protected constructor(source: string = "MessageService") {
    this.source = source;
    console.debug(`[Message Service] [${this.source}] Initializing`);
    this.initializeListeners();
    this.eventEmitter = new ReplySafeEmitter();
  }

  // Singleton instance getter
  public static getInstance(): MessageService {
    if (!this.instance) {
      this.instance = new this();
    }
    return this.instance;
  }

  // Initialize message listeners only once
  private initializeListeners() {

    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.onMessageExternal) {
      chrome.runtime.onMessageExternal.addListener((message: PostMessage, sender, sendResponse) => {
        console.debug(`[Message Service] [onMessageExternal] [${this.source}] Received external message:`, message);

        // Security gate: externally_connectable includes web.whatsapp.com, so a script
        // there can message the background directly. Auth-mutating cmds (set-token /
        // web-logout / logout) must originate ONLY from a trusted Blueticks web-app
        // origin — never WhatsApp Web — or they could inject/forge auth state.
        if (AUTH_SENSITIVE_CMDS.includes(message?.cmd) && !isTrustedAuthOrigin(sender?.origin ?? sender?.url)) {
          console.warn(`[login-flow] [Message Service] rejected external auth cmd from untrusted sender`, { cmd: message?.cmd, origin: sender?.origin, url: sender?.url });
          try { sendResponse(undefined); } catch (e) { /* ignore */ }
          return true;
        }

        // Tab auto-discard prevention (JONI approach) - prevent WhatsApp tabs from sleeping
        protectTabFromAutoDiscard(sender, "External");
        
        // Cannot wait here because the function must return true immediately.
        this.messageHandler(message, sender).then((messageResponse) => {
          // console.debug(`[Message Service] [onMessage] [${this.source}] Received message:`, message);
          try {
            sendResponse(messageResponse);
          } catch (error) {
            console.error('Failed to send response:', error);
          }
        });
        return true; // Indicate that the response will be handled asynchronously
      });
      console.debug(`[Message Service] [${this.source}] initializeListeners onMessageExternal`);
    }

    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.onMessage) {
      chrome.runtime.onMessage.addListener((message: PostMessage, sender, sendResponse) => {
        // console.debug(`[Message Service] [onMessage] [${this.source}] Received message:`, message);
        
        // Tab auto-discard prevention (JONI approach) - prevent WhatsApp tabs from sleeping
        protectTabFromAutoDiscard(sender, "Internal");
        
        // Cannot wait here because the function must return true immediately.
        this.messageHandler(message, sender).then((messageResponse) => {
          // console.debug(`[Message Service] [onMessage] [${this.source}] Sending response:`, messageResponse);
          try {
            sendResponse(messageResponse);
          } catch (error) {
            console.error('Failed to send response:', error);
          }
        });

        return true; // Indicate that the response will be handled asynchronously
      });
      console.debug(`[Message Service] [${this.source}] initializeListeners onMessage`);
    }

    if (typeof window !== "undefined") {
      console.debug(`[Message Service] [${this.source}] initializeListeners window.addEventListener`);
      window.addEventListener("message", async (event: MessageEvent) => {
        if (event?.data?.source === "react-devtools-bridge") return;
        if (!event?.data?.cmd) return;

        // Security gate: a window `message` is the page-world auth bridge (set-token /
        // web-logout). This content-script listener also runs on web.whatsapp.com, where
        // a page script could forge these to inject a token or force a logout. Only accept
        // auth bridge cmds from THIS window AND a trusted Blueticks web-app origin.
        if (AUTH_BRIDGE_CMDS.includes(event.data.cmd) && (event.source !== window || !isTrustedAuthOrigin(event.origin))) {
          console.warn(`[login-flow] [Message Service] rejected auth bridge cmd from untrusted source`, { cmd: event.data.cmd, origin: event.origin, sameWindow: event.source === window });
          return;
        }

        let response = event?.data;
        const isContactQuery = response?.cmd === 'waQueryContactsAndChatsTool';

        if (isContactQuery) {
          console.log(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] Received message cmd: ${response.cmd}, data:`, JSON.stringify(response.data || {}).substring(0, 500));
        }

        if (response && response.cmd) {
          // Handle the response and wait for it
          const messageResponse = await this.messageHandler(response);

          if (isContactQuery) {
            console.log(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] Handler returned:`, {
              responseType: typeof messageResponse,
              isUndefined: messageResponse === undefined,
              isNull: messageResponse === null,
              keys: messageResponse ? Object.keys(messageResponse) : 'N/A',
              contactsCount: (messageResponse as any)?.contacts?.length ?? 'N/A',
              chatsCount: (messageResponse as any)?.chats?.length ?? 'N/A',
              totalContacts: (messageResponse as any)?.totalContacts ?? 'N/A',
              totalChats: (messageResponse as any)?.totalChats ?? 'N/A',
              hasError: !!(messageResponse as any)?.error,
            });
          }

          // Send the response back using the response port
          const { ports } = event;
          const [responsePort] = ports;

          if (isContactQuery) {
            console.log(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] Has responsePort: ${!!responsePort}, ports count: ${ports?.length ?? 0}`);
          }

          let safeData;
          try {
            safeData = safeCloneForPostMessage(messageResponse);
            if (isContactQuery) {
              console.log(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] safeCloneForPostMessage succeeded:`, {
                safeDataType: typeof safeData,
                isUndefined: safeData === undefined,
                keys: safeData ? Object.keys(safeData) : 'N/A',
                contactsCount: safeData?.contacts?.length ?? 'N/A',
                chatsCount: safeData?.chats?.length ?? 'N/A',
              });
            }
          } catch (e) {
            console.error(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] safeCloneForPostMessage FAILED:`, e);
            console.error(`[postMessageWithResponse] [${this.source}] Error: `, messageResponse);
            console.error(e);
          }

          try {
            responsePort.postMessage(safeData);
            if (isContactQuery) {
              console.log(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] responsePort.postMessage SENT successfully`);
            }
          } catch (error) {
            if (isContactQuery) {
              console.error(`[whatsappTools.waQueryContactsAndChatsTool] [5-MSG-SERVICE] responsePort.postMessage FAILED:`, error);
            }
            console.debug(`[Message Service] Error sending message via responsePort:`, error);
            // Fallback to window.postMessage if the port is disconnected
            (window as any).postMessage(safeData, "*");
          }
        }
      });
    }

  }

  // Message handler
  // messageHandler now ensures it always calls back with a response
  private messageHandler = (message: PostMessage, sender?: unknown, _sendResponse?: unknown) => {
    return new Promise((resolve) => {
      // console.debug(`[Message Service] [${this.source}] Received message:`, message.cmd);

      // if (message.cmd === "getWaContactInfoFromListItemSelector") {
      //     console.log(`[bt-labels] [MessageService] Emitting event: ${message.cmd}`);
      // }

      // Emit the event and pass a callback to always return the response
      this.eventEmitter.emit(message.cmd, message.data, (responseMessage: any) => {
        // if (message.cmd === "getWaContactInfoFromListItemSelector") {
        //      console.log(`[bt-labels] [MessageService] Response received for: ${message.cmd}`, responseMessage);
        // }
        resolve(responseMessage);  // Always resolve with the response message
      });
    });
  };

  /**
   * Register a handler for a KNOWN command with full type-safety off the
   * MessageCommandContractMap: `args` is typed to that command's payload and
   * `sendResponse` to its result. This is the message-service counterpart to the
   * WhatsApp tools' typed registration — prefer it over the stringly-typed
   * addListener for new/migrated commands.
   */
  public onCommand<C extends MessageCommand>(
    cmd: C,
    handler: (args: MessageCommandArgs<C>, sendResponse: (response: MessageCommandResult<C>) => void) => void,
  ): void {
    this.eventEmitter.on(cmd, handler);
  }

  // Add custom event listener
  public addListener(eventType: string, listener: (...args: any[]) => void) {
    this.eventEmitter.on(eventType, listener);
  }

  // Remove custom event listener
  public removeListener(eventType: string, listener: (...args: any[]) => void) {
    this.eventEmitter.removeListener(eventType, listener);
  }
}

// Export the singleton instance of MessageService
//export const messageService = MessageService.getInstance();
